import { useState, useEffect } from "react";
import axios from "axios";
import { Briefcase, GraduationCap, Users, Target, BookOpen, ArrowRight, ExternalLink, CheckCircle2 } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ProfessionalDevelopment() {
  const [c, setC] = useState({});
  const [img, setImg] = useState({});

  useEffect(() => {
    axios.get(`${API}/site-content`).then(r => setC(r.data)).catch(() => {});
    axios.get(`${API}/site-images`).then(r => setImg(r.data)).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const resources = [
    {
      icon: Briefcase,
      title: c.pdResource1Title || "Resume & Interview Prep",
      desc: c.pdResource1Desc || "Workshops and one-on-one sessions to help members prepare for internship and full-time opportunities in healthcare business.",
    },
    {
      icon: Users,
      title: c.pdResource2Title || "Networking Events",
      desc: c.pdResource2Desc || "Connect with industry professionals, alumni, and fellow members through structured networking sessions and social events.",
    },
    {
      icon: GraduationCap,
      title: c.pdResource3Title || "Mentorship Program",
      desc: c.pdResource3Desc || "Pair with experienced professionals in healthcare consulting, policy, administration, and entrepreneurship.",
    },
    {
      icon: Target,
      title: c.pdResource4Title || "Career Panels",
      desc: c.pdResource4Desc || "Hear from professionals across healthcare business sectors about career paths, industry trends, and advice for students.",
    },
    {
      icon: BookOpen,
      title: c.pdResource5Title || "Skill-Building Workshops",
      desc: c.pdResource5Desc || "Hands-on workshops covering Excel modeling, case interviews, healthcare analytics, and presentation skills.",
    },
  ];

  return (
    <div className="min-h-screen bg-white" data-testid="professional-development-page">
      {/* Header */}
      <section className="relative bg-[#0F172A] text-white py-16 lg:py-20 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-1/4 right-1/4 w-64 h-64 bg-white rounded-full blur-[120px]" />
        </div>
        <div className="container-main relative z-10">
          <p className="text-xs font-semibold uppercase tracking-widest text-slate-400 mb-3">Member Resources</p>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Professional Development
          </h1>
          <p className="text-slate-300 mt-4 text-base max-w-2xl">
            {c.pdHeaderDesc || "Building the skills, connections, and experience you need to succeed in healthcare business."}
          </p>
        </div>
      </section>

      {/* Overview */}
      <section className="py-16 lg:py-20 bg-white" data-testid="pd-overview">
        <div className="container-main">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <div>
              <h2 className="text-2xl lg:text-3xl font-bold text-[#0F172A] tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {c.pdOverviewTitle || "Invest in Your Future"}
              </h2>
              <p className="text-slate-600 mt-4 leading-relaxed text-[15px]">
                {c.pdOverviewText || "Texas BHA's professional development programming is designed to complement your academic experience with practical skills and industry exposure. Whether you're preparing for a career in healthcare consulting, policy, or administration, our resources help you stand out."}
              </p>
              <p className="text-slate-600 mt-3 leading-relaxed text-[15px]">
                {c.pdOverviewText2 || "All professional development events are open to active Texas BHA members."}
              </p>
            </div>
            <div className="rounded-2xl overflow-hidden aspect-[4/3] bg-slate-100">
              {img.pdHero ? (
                <img src={img.pdHero} alt="Professional Development" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex flex-col items-center justify-center text-slate-300">
                  <Briefcase className="h-16 w-16 mb-3" />
                  <p className="text-sm">Upload image via admin</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      <Separator className="bg-slate-100" />

      {/* Resources Grid */}
      <section className="py-16 lg:py-20 bg-slate-50" data-testid="pd-resources">
        <div className="container-main">
          <h2 className="text-2xl font-bold text-[#0F172A] tracking-tight mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {c.pdResourcesTitle || "What We Offer"}
          </h2>
          <p className="text-slate-500 text-sm mb-10 max-w-2xl">{c.pdResourcesSubtitle || "Resources and programming designed to accelerate your career in healthcare business."}</p>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {resources.map((r, i) => (
              <Card key={`pd-${i}`} data-testid={`pd-resource-${i}`} className="bg-white border border-slate-100 shadow-sm hover:shadow-md transition-all duration-200 hover:-translate-y-0.5">
                <CardContent className="p-6">
                  <div className="w-10 h-10 rounded-lg bg-slate-50 border border-slate-100 flex items-center justify-center mb-3">
                    <r.icon className="h-5 w-5 text-[#0F172A]" />
                  </div>
                  <h3 className="font-semibold text-[15px] text-[#0F172A]" style={{ fontFamily: 'Manrope, sans-serif' }}>{r.title}</h3>
                  <p className="text-slate-500 text-sm mt-1.5 leading-relaxed">{r.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <Separator className="bg-slate-100" />

      {/* HEALTHCARE REFORM & INNOVATION Minor */}
      <section data-testid="pd-minor-section" className="py-16 lg:py-20 bg-white">
        <div className="container-main">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="flex items-center gap-2 mb-3">
                <GraduationCap className="h-5 w-5 text-[#BF5700]" />
                <p className="text-xs font-semibold uppercase tracking-widest text-slate-400">Academic Program</p>
              </div>
              <h2 className="text-2xl lg:text-3xl font-bold text-[#0F172A] tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {c.homeMinorTitle || "HEALTHCARE REFORM & INNOVATION Minor"}
              </h2>
              <p className="text-slate-600 mt-4 leading-relaxed text-[15px]">
                {c.homeMinorText || "The HEALTHCARE REFORM & INNOVATION minor at the University of Texas at Austin equips students with a multidisciplinary understanding of the healthcare industry. The program covers the financial, managerial, legal, and political dimensions of healthcare, preparing graduates for leadership roles across the sector."}
              </p>
              <p className="text-slate-600 mt-3 leading-relaxed text-[15px]">
                {c.homeMinorText2 || "As the official student organization of this minor, Texas BHA provides a hands-on complement to the academic curriculum — offering real consulting projects, policy research, and professional development opportunities."}
              </p>

              <div className="mt-6 space-y-2">
                <h3 className="font-semibold text-sm text-[#0F172A]" style={{ fontFamily: 'Manrope, sans-serif' }}>Why Pursue the Minor?</h3>
                {(c.homeMinorBullets ? c.homeMinorBullets.split(',') : ["Gain cross-functional understanding of healthcare systems", "Develop consulting, policy, and leadership skills", "Access exclusive internship and career pathways", "Join a network of healthcare-focused professionals", "Complement any major with healthcare industry expertise"]).map((item, i) => (
                  <div key={i} className="flex items-start gap-2 text-sm text-slate-600">
                    <CheckCircle2 className="h-3.5 w-3.5 text-[#0F172A] mt-0.5 shrink-0" />
                    {item.trim()}
                  </div>
                ))}
              </div>

              <a
                href="https://www.mccombs.utexas.edu/centers-initiatives/healthcare-innovation-initiative/undergrad-programs/undergraduate-healthcare-minor/"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="pd-minor-link"
              >
                <Button className="mt-6 bg-[#BF5700] hover:bg-[#A64B00] text-white rounded-full px-6 text-sm font-semibold">
                  Learn More at UT Austin <ExternalLink className="ml-2 h-4 w-4" />
                </Button>
              </a>
            </div>
            <div className="rounded-2xl overflow-hidden aspect-[4/3] bg-slate-100">
              {img.opportunitiesCampus || img.homeAbout ? (
                <img src={img.opportunitiesCampus || img.homeAbout} alt="UT Austin campus" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex flex-col items-center justify-center text-slate-300">
                  <GraduationCap className="h-16 w-16 mb-3" />
                  <p className="text-sm">Upload image via admin</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-14 bg-[#0F172A] text-white text-center" data-testid="pd-cta">
        <div className="container-main">
          <h2 className="text-xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>{c.pdCTATitle || "Ready to Level Up?"}</h2>
          <p className="text-slate-400 mt-2 max-w-md mx-auto text-sm">{c.pdCTAText || "Check back for upcoming professional development events or reach out to get involved."}</p>
        </div>
      </section>
    </div>
  );
}
